
import { Vue, Component, Prop } from "vue-property-decorator";
import { documentStore, uiStore } from "@/store/store";
import { TreeNode } from "@/types/tree";
import {
  getNodeIdAtPointInCanvas,
  shouldItBeInsertedBeforeOrAfter,
  getStackingStyle,
  getNodesIdsAtPointInCanvas
} from "@/lib/canvas/drag/element-drag";
import {
  ElementPositionAndSize,
  getTreeNodePositionAndSize
} from "@/lib/canvas/helpers";
import { distance } from "@/util/distance";
import { CanvasDragPlaceholderPosition } from "@/types/ui";
import { BuilderElement } from "@/types/elements";
import { findNode, ROOT_ELEMENT_ID, treeNodeReduce } from "@/lib/tree/tree";
import {
  findNearestParentThatWillAcceptNodes,
  findPlaceToAddNodes,
  getNestingRuleFromElementSlug
} from "@/lib/tree/nesting";
import { EasyEvent, isCanvasEvent } from "@/util/easy-event";
import { fold, right } from "fp-ts/lib/Either";
import { pipe } from "fp-ts/lib/function";

type PlaceholderPosition = {
  dropTargetId: number;
  dragPlaceholderIndex: number;
  beforeOrAfter: "before" | "after";
}; // this is a silly type because we already have a very similar one - CanvasDragPlaceholderPosition

type DragPayload2 = {
  draggedElementTypeSlug: string;
  droppedOnElementId: number;
  index: number;
};

// TODO: this file is nearly the same as OnCanvasDragHandle, just a dozen or less lines
// of difference. Merge them together somehow (there's a closed PR for this)
@Component
export default class InAddDragHandle extends Vue {
  @Prop()
  element!: BuilderElement;

  lastSubmittedDrag: false | DragPayload2 | "add" = false;

  easyEvent = new EasyEvent();

  get selfNestingType() {
    return getNestingRuleFromElementSlug(this.element.slug);
  }

  dragStart(e: MouseEvent) {
    e.stopPropagation();

    this.easyEvent.on("mousemove", this.dragging);
    this.easyEvent.on("mouseup", this.dragEnd);

    uiStore.setAddElementDraggedSlug(this.element.slug);
  }

  setCanvasDragPlaceholderPosition(
    canvasDragPlaceholderPosition: CanvasDragPlaceholderPosition | null
  ) {
    uiStore.setCanvasDragPlaceholderPosition(canvasDragPlaceholderPosition);
  }

  dragging(e: MouseEvent) {
    e.stopPropagation();

    if (!isCanvasEvent(e)) {
      this.setDragPosition(false);
      return;
    }

    const mouseX = e.clientX;
    const mouseY = e.clientY;

    const nodeMouseIsOverId = getNodeIdAtPointInCanvas(mouseX, mouseY);

    if (nodeMouseIsOverId === undefined) {
      this.setDragPositionToPlaceToAddNodes();
      return;
    }

    const nodeMouseIsOver = findNode(
      documentStore.document.tree,
      nodeMouseIsOverId
    );

    if (nodeMouseIsOver === undefined) {
      this.setDragPosition(false);
      return;
    }

    const dropTargetNode = findNearestParentThatWillAcceptNodes(
      documentStore.document.tree,
      nodeMouseIsOver,
      {
        self: this.selfNestingType,
        children: []
      }
    );

    if (!dropTargetNode || !dropTargetNode.id) {
      this.setDragPosition(false);
      return;
    }

    // ---------------------------------

    if (nodeMouseIsOverId === dropTargetNode.id) {
      /*
      if we are over the drop target, but not any of its children...
      */
      this.handleDragWhenMouseIsOverTheDropTargetButNotOverAChildElementOfTheDropTarget(
        mouseX,
        mouseY,
        nodeMouseIsOverId,
        this.element.slug,
        dropTargetNode
      );
    } else if (dropTargetNode.children.find(n => nodeMouseIsOverId === n.id)) {
      /*
      if we are over a child of the drop target, and not the self
      */
      this.handleDragWhenMouseIsOverAChildOfTheDropTarget(
        mouseX,
        mouseY,
        nodeMouseIsOverId,
        this.element.slug,
        dropTargetNode
      );
    } else if (
      /*
        if we are over a deeper child of the drop target, and not the self
        */
      treeNodeReduce(
        (node, foundNode: TreeNode | undefined) => {
          if (!foundNode) {
            foundNode = node.children.find(n => nodeMouseIsOverId === n.id);
          }
          return foundNode;
        },
        dropTargetNode,
        undefined
      )
    ) {
      const childOfDropTargetThatMouseIsReallyOver = getNodesIdsAtPointInCanvas(
        mouseX,
        mouseY
      ).reduce((foundNode: TreeNode | undefined, nodeId: number) => {
        if (!foundNode) {
          return dropTargetNode.children.find(n => n.id === nodeId);
        }
        return foundNode;
      }, undefined);

      if (childOfDropTargetThatMouseIsReallyOver) {
        this.handleDragWhenMouseIsOverAChildOfTheDropTarget(
          mouseX,
          mouseY,
          childOfDropTargetThatMouseIsReallyOver.id,
          this.element.slug,
          dropTargetNode
        );
      }
    }
  }

  dragEnd(e: MouseEvent) {
    e.stopPropagation();

    if (isCanvasEvent(e) && this.lastSubmittedDrag) {
      if (this.lastSubmittedDrag === "add") {
        documentStore.addElement({
          elementSlug: this.element.slug,
          addToNodeId: ROOT_ELEMENT_ID
        });
      } else {
        const droppedOnElement = findNode(
          documentStore.document.tree,
          this.lastSubmittedDrag.droppedOnElementId
        );

        if (!droppedOnElement) return;

        documentStore.addElement({
          elementSlug: this.element.slug,
          placeToAdd: right({
            index: this.lastSubmittedDrag.index,
            place: droppedOnElement
          })
        });
      }
    }

    this.dragEndComplete();
  }

  dragEndComplete() {
    this.easyEvent.off("mousemove", this.dragging);
    this.easyEvent.off("mouseup", this.dragEnd);

    this.setCanvasDragPlaceholderPosition(null);
    this.lastSubmittedDrag = false;
    uiStore.setAddElementDraggedSlug(null);
  }

  setDragPosition(dragPosition: DragPayload2 | false) {
    if (dragPosition === false) {
      uiStore.setCanvasDragPlaceholderPosition(null);
    }

    this.lastSubmittedDrag = dragPosition;
  }

  setDragPositionToPlaceToAddNodes() {
    this.lastSubmittedDrag = "add";

    const placeToAdd = findPlaceToAddNodes(
      documentStore.document.tree,
      ROOT_ELEMENT_ID,
      {
        self: getNestingRuleFromElementSlug(this.element.slug),
        children: []
      }
    );

    // todo - this isn't wonderful, in fact, when would place to add ever be section? when the page is blank?
    // maybe some other cases. if the page is blank, the placeholder doesn't appear anyway though.

    pipe(
      placeToAdd,
      fold(
        () => {},
        legalPlaceToAdd => {
          if (legalPlaceToAdd === "TOP_LEVEL") {
            uiStore.setCanvasDragPlaceholderPosition({
              parentId: ROOT_ELEMENT_ID,
              nodeIndex: documentStore.document.tree.root.children.length - 1,
              position: "after",
              onlyShowTargetOutline: false
            });
          } else {
            uiStore.setCanvasDragPlaceholderPosition({
              parentId: legalPlaceToAdd.place.id,
              nodeIndex: legalPlaceToAdd.index
                ? legalPlaceToAdd.index
                : legalPlaceToAdd.place.children.length - 1,
              position: "after",
              onlyShowTargetOutline: false
            });
          }
        }
      )
    );
  }

  //**** TODO: these super long functions can be extracted to be reused here and in OnDragWhatever since they're the same
  handleDragWhenMouseIsOverAChildOfTheDropTarget(
    mouseX: number,
    mouseY: number,
    mouseOverNodeId: number,
    draggedNodeSlug: string,
    dropTargetNode: TreeNode,
    placeholderOnly = false
  ) {
    const stackingStyle = getStackingStyle(dropTargetNode);

    const insertBeforeOrAfter =
      shouldItBeInsertedBeforeOrAfter(
        mouseX,
        mouseY,
        mouseOverNodeId,
        stackingStyle
      ) === "before"
        ? "before"
        : "after";

    const nodesInDropTarget = dropTargetNode.children.map(node => node.id);
    const dragPlaceholderIndex = dropTargetNode.children
      .map(node => node.id)
      .findIndex(id => id === mouseOverNodeId);

    let dropPlacementIndex = nodesInDropTarget.findIndex(
      id => id === mouseOverNodeId
    );

    if (insertBeforeOrAfter === "after") {
      dropPlacementIndex++; // for the placement of the dropped element, 0 inserts before the first node, 1 inserts after the first node
    }

    if (placeholderOnly) {
      this.setPlaceholder({
        dropTargetId: dropTargetNode.id,
        dragPlaceholderIndex: dragPlaceholderIndex,
        beforeOrAfter: insertBeforeOrAfter
      });

      this.setDragPosition(false);
    } else {
      this.setPlaceholder({
        dropTargetId: dropTargetNode.id,
        dragPlaceholderIndex: dragPlaceholderIndex,
        beforeOrAfter: insertBeforeOrAfter
      });

      this.setDragPosition({
        draggedElementTypeSlug: draggedNodeSlug,
        droppedOnElementId: dropTargetNode.id,
        index: dropPlacementIndex
      });
    }
  }

  handleDragWhenMouseIsOverTheDropTargetButNotOverAChildElementOfTheDropTarget(
    mouseX: number,
    mouseY: number,
    _mouseOverNodeId: number,
    draggedNodeSlug: string,
    dropTargetNode: TreeNode
  ) {
    if (dropTargetNode.children.length === 0) {
      this.setPlaceholder({
        dropTargetId: dropTargetNode.id,
        dragPlaceholderIndex: 0,
        beforeOrAfter: "after"
      });

      this.setDragPosition({
        draggedElementTypeSlug: draggedNodeSlug,
        droppedOnElementId: dropTargetNode.id,
        index: 0
      });
    } else {
      const rects = (
        dropTargetNode.children
          .map((node, i) => {
            return {
              positionAndSize: getTreeNodePositionAndSize(node, "absolute"), // we need to save the index to figure out which rect,
              index: i,
              id: node.id
            };
          })
          .filter(r => r.positionAndSize) as {
          index: number;
          positionAndSize: ElementPositionAndSize;
          id: number;
        }[]
      )
        .map(r => {
          return {
            min: {
              x: r.positionAndSize.x,
              y: r.positionAndSize.y
            },
            max: {
              x: r.positionAndSize.x + r.positionAndSize.width,
              y: r.positionAndSize.y + r.positionAndSize.height
            },
            nodeIndex: r.index,
            id: r.id
          };
        })
        .map(r => {
          return {
            ...r,
            distance: distance(r, { x: mouseX, y: mouseY })
          };
        });

      const nearestRect = rects.reduce((r1, r2) =>
        r1.distance < r2.distance ? r1 : r2
      );

      const xCenter = (nearestRect.min.x + nearestRect.max.x) / 2;
      const yCenter = (nearestRect.min.y + nearestRect.max.y) / 2;

      const leftOrRight = xCenter > mouseX ? "left" : "right";
      const aboveOrBelow = yCenter > mouseY ? "above" : "below";

      const stackingStyle = getStackingStyle(dropTargetNode);

      let insertBeforeOrAfter: "before" | "after";

      if (stackingStyle === "vertical") {
        insertBeforeOrAfter = aboveOrBelow === "above" ? "before" : "after";
      } else {
        insertBeforeOrAfter = leftOrRight === "left" ? "before" : "after";
      }

      const nodesInDropTargetIds = dropTargetNode.children.map(node => node.id);

      let dropPlacementIndex = nodesInDropTargetIds.findIndex(
        id => id === nearestRect.id
      );

      if (insertBeforeOrAfter === "after") {
        dropPlacementIndex++;
      }

      this.setPlaceholder({
        dropTargetId: dropTargetNode.id,
        dragPlaceholderIndex: nearestRect.nodeIndex,
        beforeOrAfter: insertBeforeOrAfter
      });

      this.setDragPosition({
        draggedElementTypeSlug: draggedNodeSlug,
        droppedOnElementId: dropTargetNode.id,
        index: dropPlacementIndex
      });
    }
  }

  setPlaceholder(placeholderPosition: PlaceholderPosition) {
    this.movePlaceholder(
      placeholderPosition.dropTargetId,
      placeholderPosition.dragPlaceholderIndex,
      placeholderPosition.beforeOrAfter
    );
  }

  movePlaceholder(
    dropTargetId: number,
    dragPlaceholderIndex: number,
    beforeOrAfter: "before" | "after"
  ) {
    const onlyShowTargetOutline = false;

    /* only move if it is materially different than last position */
    if (uiStore.canvasDragPlaceholderPosition) {
      const movedForwards = () =>
        dragPlaceholderIndex ===
        (uiStore.canvasDragPlaceholderPosition as CanvasDragPlaceholderPosition)
          .nodeIndex +
          1;

      const movedBackwards = () =>
        dragPlaceholderIndex ===
        (uiStore.canvasDragPlaceholderPosition as CanvasDragPlaceholderPosition)
          .nodeIndex -
          1;

      if (uiStore.canvasDragPlaceholderPosition.parentId === dropTargetId) {
        if (
          movedForwards() &&
          uiStore.canvasDragPlaceholderPosition.position === "after" &&
          beforeOrAfter === "before"
        ) {
          return;
        }

        if (
          movedBackwards() &&
          uiStore.canvasDragPlaceholderPosition.position === "before" &&
          beforeOrAfter === "after"
        ) {
          return;
        }
      }
    }
    /* ------ */

    this.setCanvasDragPlaceholderPosition({
      parentId: dropTargetId,
      nodeIndex: dragPlaceholderIndex,
      position: beforeOrAfter,
      onlyShowTargetOutline: onlyShowTargetOutline
    });
  }

  // =--__-----
}
